import React, { useMemo, useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon, Columns3Icon, MoreHorizontalIcon } from 'lucide-react';
import { AdminEmptyState, Pagination } from './AdminPrimitives';
import { Skeleton } from '../Skeleton';
import { useLocale } from '../../contexts/LocaleContext';
export interface Column<T> {
  id: string;
  header: string;
  /** Rendered cell. */
  cell: (row: T) => React.ReactNode;
  /** Sort key. Omit to make the column unsortable. */
  sortValue?: (row: T) => string | number;
  width?: string;
  /** Hidden by default but available in column visibility. */
  optional?: boolean;
  align?: 'left' | 'right';
}
export interface RowAction<T> {
  id: string;
  label: string;
  onSelect: (row: T) => void;
  destructive?: boolean;
}
interface DataTableProps<T> {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  loading?: boolean;
  /** Defaults to the localized generic empty copy. */
  emptyTitle?: string;
  emptyBody?: string;
  actions?: RowAction<T>[];
  /** Enables row selection plus the bulk action bar. */
  selectable?: boolean;
  bulkActions?: {
    id: string;
    label: string;
    onSelect: (ids: string[]) => void;
  }[];
  pageSize?: number;
  toolbar?: React.ReactNode;
  onRowClick?: (row: T) => void;
}
export function DataTable<T>({
  rows,
  columns,
  rowKey,
  loading = false,
  emptyTitle,
  emptyBody,
  actions,
  selectable = false,
  bulkActions,
  pageSize = 8,
  toolbar,
  onRowClick
}: DataTableProps<T>) {
  const {
    t
  } = useLocale();
  const [sort, setSort] = useState<{
    id: string;
    dir: 'asc' | 'desc';
  } | null>(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string[]>([]);
  const [hidden, setHidden] = useState<string[]>(columns.filter(column => column.optional).map(column => column.id));
  const [showColumns, setShowColumns] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const visible = columns.filter(column => !hidden.includes(column.id));
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const column = columns.find(item => item.id === sort.id);
    if (!column?.sortValue) return rows;
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = column.sortValue!(a);
      const bv = column.sortValue!(b);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * factor;
      return String(av).localeCompare(String(bv)) * factor;
    });
  }, [rows, sort, columns]);
  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const current = Math.min(page, pageCount);
  const pageRows = sorted.slice((current - 1) * pageSize, current * pageSize);
  const pageIds = pageRows.map(rowKey);
  const allSelected = pageIds.length > 0 && pageIds.every(id => selected.includes(id));
  const toggleSort = (id: string) => setSort(prev => prev?.id === id ? {
    id,
    dir: prev.dir === 'asc' ? 'desc' : 'asc'
  } : {
    id,
    dir: 'asc'
  });
  return <div className="rounded-card border border-sand-line bg-white/80" data-id="element-450" data-mp="2dw614knshtk6">
      {(toolbar || columns.some(column => column.optional)) && <div className="flex flex-wrap items-center gap-2 border-b border-sand-line p-2" data-id="element-451" data-mp="2iswj5dj5mz88">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2" data-id="element-452" data-mp="2nlzg7vn54qt6">{toolbar}</div>
          {columns.some(column => column.optional) && <div className="relative" data-id="element-453" data-mp="1ux1ypuqr6y1h">
              <button type="button" onClick={() => setShowColumns(prev => !prev)} aria-expanded={showColumns} className="flex h-10 items-center gap-2 rounded-card border border-sand-line bg-white px-2 text-[12.5px] font-medium text-deep transition-colors duration-150 ease-brand hover:border-deep/30" data-id="element-454" data-mp="1uy3jiqxkmywe">
                <Columns3Icon size={15} aria-hidden="true" data-id="element-455" data-mp="3g93cnc863wtc" />
                {t('adm.action.columns')}
              </button>
              {showColumns && <div className="absolute right-0 top-[44px] z-20 w-[230px] rounded-card border border-sand-line bg-sand p-2 shadow-lift" data-id="element-456" data-mp="3iukvofvoghgb">
                  <ul data-id="element-457" data-mp="3dwjddx62sm5w">
                    {columns.map(column => <li key={column.id} data-id="element-458" data-mp="3ssh6f6kpdadw">
                        <label className="flex min-h-[34px] items-center gap-2 text-[12.5px] text-deep" data-id="element-459" data-mp="3stik7it3sazb">
                          <input type="checkbox" checked={!hidden.includes(column.id)} onChange={() => setHidden(prev => prev.includes(column.id) ? prev.filter(id => id !== column.id) : [...prev, column.id])} className="h-4 w-4 accent-[color:var(--bee-gold)]" data-id="element-460" data-mp="3ns2x7l7d7uea" />
                          {column.header}
                        </label>
                      </li>)}
                  </ul>
                </div>}
            </div>}
        </div>}

      {selectable && selected.length > 0 && bulkActions && <div className="flex flex-wrap items-center gap-2 border-b border-sand-line bg-sand-deep/50 px-3 py-2" data-id="element-461" data-mp="tzrle4qljdjn">
          <p className="bee-num text-[12px] font-medium text-deep" data-id="element-462" data-mp="yy2wsjwjhw3z">
            {t('adm.table.selected', {
          n: selected.length
        })}
          </p>
          {bulkActions.map(action => <button key={action.id} type="button" onClick={() => {
        action.onSelect(selected);
        setSelected([]);
      }} className="h-8 rounded-card border border-sand-line bg-white px-2 text-[12px] font-medium text-deep transition-colors duration-150 ease-brand hover:border-deep/30" data-id="element-463" data-mp="p287j5xt92s8">
              {action.label}
            </button>)}
          <button type="button" onClick={() => setSelected([])} className="h-8 px-2 text-[12px] font-medium text-muted transition-colors duration-150 ease-brand hover:text-deep" data-id="element-464" data-mp="2804kmysei7pa">
            {t('adm.action.clear')}
          </button>
        </div>}

      {loading ? <div className="space-y-2 p-3" data-id="element-465" data-mp="1u9ix2ws059l2">
          {Array.from({
        length: 6
      }).map((_, index) => <Skeleton key={index} className="h-[46px] w-full" data-id="element-466" data-mp="2qza4fwle6r8a" data-mpi={index} />)}
        </div> : pageRows.length === 0 ? <div className="p-3" data-id="element-467" data-mp="2mxwvveqx09xo">
          <AdminEmptyState title={emptyTitle ?? t('adm.table.empty')} body={emptyBody ?? t('adm.table.emptyBody')} data-id="element-468" data-mp="2mvkhfbkd8hpd" />
        </div> : <div className="overflow-x-auto" data-id="element-469" data-mp="2cypmt3r7r1gd">
          <table className="w-full min-w-[720px] border-collapse text-left" data-id="element-470" data-mp="16ivo086codwx">
            <thead data-id="element-471" data-mp="16gsngu7kn2dy">
              <tr className="border-b border-sand-line" data-id="element-472" data-mp="11kbxrvkq4fyr">
                {selectable && <th scope="col" className="w-[44px] px-3 py-2" data-id="element-473" data-mp="rg592t40zc28">
                    <input type="checkbox" aria-label={t('adm.table.selectPage')} checked={allSelected} onChange={() => setSelected(prev => allSelected ? prev.filter(id => !pageIds.includes(id)) : [...new Set([...prev, ...pageIds])])} className="h-4 w-4 accent-[color:var(--bee-gold)]" data-id="element-474" data-mp="rhyk0klo8ig1" />
                  </th>}
                {visible.map(column => <th key={column.id} scope="col" style={{
              width: column.width
            }} className={`px-3 py-2 ${column.align === 'right' ? 'text-right' : ''}`} data-id="element-475" data-mp="1p45prxtpogtz">
                    {column.sortValue ? <button type="button" onClick={() => toggleSort(column.id)} className="bee-overline inline-flex items-center gap-1 text-[8.5px] font-bold text-muted transition-colors duration-150 ease-brand hover:text-deep" data-id="element-476" data-mp="1wmn189l5e5pl">
                        {column.header}
                        {sort?.id === column.id && (sort.dir === 'asc' ? <ArrowUpIcon size={11} data-id="element-477" data-mp="18pppwivfc22x" /> : <ArrowDownIcon size={11} data-id="element-478" data-mp="18rsqtxy47fmf" />)}
                      </button> : <span className="bee-overline text-[8.5px] font-bold text-muted" data-id="element-479" data-mp="1indwc62e6y0x">
                        {column.header}
                      </span>}
                  </th>)}
                {actions && <th scope="col" className="w-[56px] px-3 py-2" data-id="element-480" data-mp="2u299fnle4rfd" />}
              </tr>
            </thead>
            <tbody data-id="element-481" data-mp="31kqr4h3f7cjf">
              {pageRows.map(row => {
            const id = rowKey(row);
            return <tr key={id} onClick={onRowClick ? () => onRowClick(row) : undefined} className={`border-b border-sand-line last:border-b-0 transition-colors duration-150 ease-brand hover:bg-sand-deep/35 ${onRowClick ? 'cursor-pointer' : ''}`} data-id="element-482" data-mp="36j20kqpszl14">
                    {selectable && <td className="px-3 py-2" onClick={event => event.stopPropagation()} data-id="element-483" data-mp="3an00v4qv71vr">
                        <input type="checkbox" aria-label={t('adm.table.select', {
                  id
                })} checked={selected.includes(id)} onChange={() => setSelected(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id])} className="h-4 w-4 accent-[color:var(--bee-gold)]" data-id="element-484" data-mp="3fm320e3q4nyg" />
                      </td>}
                    {visible.map(column => <td key={column.id} className={`px-3 py-2 align-middle text-[12.5px] text-deep ${column.align === 'right' ? 'text-right' : ''}`} data-id="element-485" data-mp="3jlymhfxkr7i4">
                        {column.cell(row)}
                      </td>)}
                    {actions && <td className="relative px-3 py-2 text-right" onClick={event => event.stopPropagation()} data-id="element-486" data-mp="30n48i95xqb30">
                        <button type="button" aria-label={t('adm.action.actions')} aria-expanded={openMenu === id} onClick={() => setOpenMenu(openMenu === id ? null : id)} className="grid h-9 w-9 place-items-center rounded-card text-muted transition-colors duration-150 ease-brand hover:bg-sand-deep hover:text-deep" data-id="element-487" data-mp="2m0nng3ngfb9x">
                          <MoreHorizontalIcon size={17} data-id="element-488" data-mp="34xot7qou1fex" />
                        </button>
                        {openMenu === id && <div className="absolute right-2 top-[46px] z-20 w-[220px] rounded-card border border-sand-line bg-sand p-1 text-left shadow-lift" data-id="element-489" data-mp="3eq4a0koe7xy8">
                            <ul data-id="element-490" data-mp="39skxpqb99v8x">
                              {actions.map(action => <li key={action.id} data-id="element-491" data-mp="3oo0onmgjhb13">
                                  <button type="button" onClick={() => {
                        action.onSelect(row);
                        setOpenMenu(null);
                      }} className={`flex min-h-[36px] w-full items-center rounded-[8px] px-2 text-[12.5px] font-medium transition-colors duration-150 ease-brand hover:bg-sand-deep ${action.destructive ? 'text-gold-deep' : 'text-deep'}`} data-id="element-492" data-mp="3jpgz7c2qlh3z">
                                    {action.label}
                                  </button>
                                </li>)}
                            </ul>
                          </div>}
                      </td>}
                  </tr>;
          })}
            </tbody>
          </table>
        </div>}

      {!loading && sorted.length > pageSize && <Pagination page={current} pageCount={pageCount} total={sorted.length} onPage={setPage} data-id="element-493" data-mp="2mivr3n1hbo3c" />}
    </div>;
}